import { FC } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { InferType, object, string } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Button, Modal, RadioGroup, Text, TextInput } from '@mrshmllw/smores-react'

import { OptionItem } from 'types'
import { TrackingFunction } from 'utils'

const requiredMessage = 'Please answer this question to continue'

const formSchema = object({
  noRegReason: string().required(requiredMessage),
  otherReason: string().when('noRegReason', {
    is: 'Other',
    then: () => string().required(requiredMessage),
    otherwise: () => string().nullable().optional(),
  }),
})

type FormValues = InferType<typeof formSchema>

const reasonOptions: OptionItem[] = [
  {
    label: 'I have not purchased the vehicle yet',
    value: 'I have not purchased the vehicle yet',
  },
  {
    label: 'The car is not yet registered',
    value: 'The car is not yet registered',
  },
  {
    label: 'Just browsing',
    value: 'Just browsing',
  },
  {
    label: 'Other',
    value: 'Other',
  },
]

interface ExtraProps {
  track: TrackingFunction
  hasSubmittedSurvey: boolean
  setHasSubmittedSurvey: (submitted: boolean) => void
}

// Creates props from the Modal component as we don't expose these from Smores
type Props = Parameters<typeof Modal>[0] & ExtraProps

export const ExitSurveyModal: FC<Props> = ({
  showModal,
  handleClick,
  track,
  hasSubmittedSurvey,
  setHasSubmittedSurvey,
}) => {
  const { handleSubmit, control, watch } = useForm<FormValues>({
    defaultValues: { noRegReason: undefined },
    resolver: yupResolver(formSchema),
  })

  const watchNoRegReason = watch('noRegReason')

  const onSubmit = (data: FormValues) => {
    const reason = data.noRegReason === 'Other' ? data.otherReason : data.noRegReason
    track('NoRegistrationReason', { reason })
    setHasSubmittedSurvey(true)
  }

  const modalTitle = hasSubmittedSurvey
    ? 'Thank you. We’ll confirm your price when you have the registration plate. '
    : 'No problem! Please tell us why.'

  return (
    <Modal cross handleClick={handleClick} title={modalTitle} showModal={showModal}>
      {hasSubmittedSurvey ? (
        <>
          <Box as="section" maxWidth="345px" mb="32px" mt="16px">
            <Text typo="body-regular" mb="16px">
              Without a registration plate, the price you see is just a guide. To buy your
              Marshmallow policy, please come back when you&apos;ve confirmed the plate.
            </Text>
            <Text typo="body-regular">
              We&apos;ve sent you an email with a link to bring you back here when you&apos;re
              ready.
            </Text>
          </Box>
          <Button type="button" forcedWidth="100%" onClick={handleClick}>
            close
          </Button>
        </>
      ) : (
        <form
          id="exit-survey-form"
          onSubmit={(event) => {
            // If the form is ever used inside another form, this stops the parent form being submitted as well
            event.preventDefault()
            event.stopPropagation()
            handleSubmit(onSubmit)()
          }}
        >
          <Controller
            name="noRegReason"
            control={control}
            render={({ field: { ref, onChange, value }, fieldState: { error } }) => (
              <RadioGroup
                mt="16px"
                mb="24px"
                ref={ref}
                label=""
                data-testid="noRegReason"
                displayType="vertical-card"
                value={value}
                options={reasonOptions}
                onChange={onChange}
                error={!!error}
                errorMsg={error?.message}
              />
            )}
          />
          {watchNoRegReason === 'Other' && (
            <Controller
              name="otherReason"
              control={control}
              render={({ field: { ref, onChange, value }, fieldState: { error } }) => (
                <TextInput
                  ref={ref}
                  mb="24px"
                  label="Please give another reason"
                  placeholder="Enter details here..."
                  onChange={onChange}
                  value={value || ''}
                  error={!!error}
                  errorMsg={error?.message}
                />
              )}
            />
          )}
          <Button type="submit" forcedWidth="100%">
            Continue
          </Button>
        </form>
      )}
    </Modal>
  )
}
